import type { TextInputInstance } from 'react-native';
import { useState, useRef, useEffect } from 'react'
import {
    View,
    TextInput,
    Text,
    StyleSheet,
    ActivityIndicator,
    Platform,
    useWindowDimensions,
} from 'react-native'
import { supabase } from '../lib/supabase'
import { captureExceptionIfNotNetwork, isExpectedOtpVerificationError } from '../lib/sentryUtils'
import colors from '../lib/colors'
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

interface OtpCodeInputProps {
    email: string
    onVerified: () => Promise<void> | void
    otpType?: 'email' | 'email_change'
    onVerificationIssue?: (issue: OtpVerificationIssueKind | null) => void
}

export type OtpVerificationIssueKind = 'invalid_or_expired' | 'unexpected'

const NUM_DIGITS = 6

export default function OtpCodeInput({
    email,
    onVerified,
    otpType = 'email',
    onVerificationIssue,
}: OtpCodeInputProps) {
    const { i18n } = useLingui()
    const { width: viewportWidth } = useWindowDimensions()
    const useCompactDigits = viewportWidth < 420
    const useVeryCompactDigits = viewportWidth < 360
    const [digits, setDigits] = useState<string[]>(Array(NUM_DIGITS).fill(''))
    const [isVerifying, setIsVerifying] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
    const refs = useRef<(TextInputInstance | null)[]>(Array(NUM_DIGITS).fill(null))
    const isMountedRef = useRef(true)
    const isVerifyingRef = useRef(false)
    const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Auto-focus first box on mount
    useEffect(() => {
        scheduleFocusFirstInput()

        return () => {
            isMountedRef.current = false

            if (focusTimerRef.current) {
                clearTimeout(focusTimerRef.current)
            }
        }
    }, [])

    const scheduleFocusFirstInput = () => {
        if (focusTimerRef.current) {
            clearTimeout(focusTimerRef.current)
        }

        focusTimerRef.current = setTimeout(() => refs.current[0]?.focus(), 100)
    }

    const clearAndRefocus = () => {
        setDigits(Array(NUM_DIGITS).fill(''))
        scheduleFocusFirstInput()
    }

    const clearVerificationIssue = () => {
        setError(null)
        onVerificationIssue?.(null)
    }

    const reportVerificationIssue = (issue: OtpVerificationIssueKind, message: string) => {
        setError(message)
        onVerificationIssue?.(issue)
    }

    const verify = async (code: string) => {
        if (isVerifyingRef.current) {
            return
        }

        isVerifyingRef.current = true
        setIsVerifying(true)
        clearVerificationIssue()

        try {
            const { error: otpError } = await supabase.auth.verifyOtp({
                email,
                token: code,
                type: otpType,
            })

            if (otpError) {
                const isExpectedIssue = isExpectedOtpVerificationError(otpError)

                if (!isExpectedIssue) {
                    captureExceptionIfNotNetwork(new Error(otpError.message || 'OTP verification failed'), {
                        tags: { operation: 'verify_otp' },
                        extra: {
                            errorMessage: otpError.message,
                            errorCode: otpError.code,
                        },
                    })
                }

                reportVerificationIssue(
                    isExpectedIssue ? 'invalid_or_expired' : 'unexpected',
                    isExpectedIssue
                        ? 'Invalid or expired code. Please try again.'
                        : 'Something went wrong. Please try again.'
                )
                clearAndRefocus()
            } else {
                onVerificationIssue?.(null)
                await onVerified()
            }
        } catch (err: any) {
            captureExceptionIfNotNetwork(err, {
                tags: { operation: 'verify_otp' },
                extra: { errorMessage: err.message },
            })
            reportVerificationIssue('unexpected', 'Something went wrong. Please try again.')
            clearAndRefocus()
        } finally {
            isVerifyingRef.current = false

            if (isMountedRef.current) {
                setIsVerifying(false)
            }
        }
    }

    const handleChangeText = (text: string, index: number) => {
        if (isVerifyingRef.current) {
            return
        }

        clearVerificationIssue()

        // Paste support: if pasted text has multiple digits, fill all boxes
        if (text.length >= NUM_DIGITS) {
            const pastedDigits = text.replace(/\D/g, '').slice(0, NUM_DIGITS).split('')
            if (pastedDigits.length === NUM_DIGITS) {
                setDigits(pastedDigits)
                refs.current[NUM_DIGITS - 1]?.focus()
                verify(pastedDigits.join(''))
                return
            }
        }

        // Single digit entry
        const digit = text.replace(/\D/g, '').slice(-1)
        const newDigits = [...digits]
        newDigits[index] = digit
        setDigits(newDigits)

        if (digit && index < NUM_DIGITS - 1) {
            refs.current[index + 1]?.focus()
        }

        // Auto-submit when all filled
        if (digit && newDigits.every(d => d !== '')) {
            verify(newDigits.join(''))
        }
    }

    const handleKeyPress = (e: any, index: number) => {
        if (isVerifyingRef.current) {
            return
        }

        if (e.nativeEvent.key === 'Backspace' && !digits[index] && index > 0) {
            const newDigits = [...digits]
            newDigits[index - 1] = ''
            setDigits(newDigits)
            refs.current[index - 1]?.focus()
        }
    }

    return (
        <View style={styles.container}>
            <View style={[
                styles.digitRow,
                useCompactDigits && styles.digitRowCompact,
                useVeryCompactDigits && styles.digitRowVeryCompact,
            ]}>
                {digits.map((digit, index) => (
                    <TextInput
                        key={index}
                        testID={`otp-digit-${index}`}
                        ref={(el) => { refs.current[index] = el }}
                        style={[
                            styles.digitBox,
                            useCompactDigits && styles.digitBoxCompact,
                            useVeryCompactDigits && styles.digitBoxVeryCompact,
                            focusedIndex === index && styles.digitBoxFocused,
                            error && styles.digitBoxError,
                        ]}
                        value={digit}
                        onChangeText={(text) => handleChangeText(text, index)}
                        onKeyPress={(e) => handleKeyPress(e, index)}
                        onFocus={() => setFocusedIndex(index)}
                        onBlur={() => setFocusedIndex(null)}
                        keyboardType="number-pad"
                        maxLength={index === 0 ? 6 : 1}
                        textContentType="oneTimeCode"
                        autoComplete="one-time-code"
                        accessibilityLabel={i18n._({
                            ...msg({
                            id: 'auth.otp.digitLabel',
                            message: 'Verification code digit {position} of {count}',
                            }),
                            values: { position: index + 1, count: NUM_DIGITS },
                        })}
                        accessibilityHint={index === 0
                            ? i18n._(msg({ id: 'auth.otp.pasteHint', message: 'You can paste the complete 6-digit code here.' }))
                            : undefined}
                        editable={!isVerifying}
                        selectTextOnFocus
                    />
                ))}
            </View>

            {isVerifying && (
                <View style={styles.verifyingContainer}>
                    <ActivityIndicator size="small" color={colors.primaryGreen} />
                    <Text style={styles.verifyingText}><Trans id="otpcodeinput.verifying" comment="UI copy in components/OtpCodeInput.tsx">Verifying...</Trans></Text>
                </View>
            )}

            {error && !onVerificationIssue && (
                <Text style={styles.errorText}>{error}</Text>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        width: '100%',
        marginVertical: 16,
    },
    digitRow: {
        flexDirection: 'row',
        gap: 8,
        justifyContent: 'center',
    },
    digitRowCompact: {
        gap: 6,
    },
    digitRowVeryCompact: {
        gap: 4,
    },
    digitBox: {
        width: 48,
        height: 56,
        borderRadius: 8,
        borderWidth: 1.5,
        borderColor: colors.border,
        backgroundColor: '#ffffff',
        fontSize: 24,
        fontWeight: '600',
        textAlign: 'center',
        color: colors.text900,
        ...(Platform.OS === 'web' && {
            outlineWidth: 0,
        }),
    },
    digitBoxCompact: {
        width: 44,
        height: 52,
        fontSize: 22,
    },
    digitBoxVeryCompact: {
        width: 36,
        height: 48,
        fontSize: 20,
    },
    digitBoxFocused: {
        borderColor: colors.primaryGreen,
    },
    digitBoxError: {
        borderColor: colors.errorRed,
    },
    verifyingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 12,
        gap: 8,
    },
    verifyingText: {
        fontSize: 14,
        color: colors.text600,
    },
    errorText: {
        fontSize: 14,
        color: colors.errorRed,
        marginTop: 12,
        textAlign: 'center',
    },
})
